/* ============================================
   POSSESSION PHASE TRACKER
   ============================================
   Tracks possession phases incrementally.
   Each phase accumulates events, players, and
   zone progression. Resets on possession change.

   Zones come directly from the MQTT event types:
   - Possession = own half (defense)
   - Attaque = opponent half (attack)
   - Attaque dangereuse = near opponent goal (dangerous-attack)
   ============================================ */

import { state } from '../state.js'

const MAX_ARCHIVED_PHASES = 3

// Events that force a new phase (set-piece restarts)
const PHASE_RESET_EVENTS = [
  "Coup d'envoi",
  "Coup d'envoi 2ème mi-temps"
]

// Events that count as ball progression
const PROGRESSION_EVENTS = [
  'Possession',
  'Attaque',
  'Attaque dangereuse'
]

// Map event types to zones (from MQTT data directly)
const EVENT_TO_ZONE = {
  Possession: 'own-half',
  Attaque: 'attack',
  'Attaque dangereuse': 'dangerous-attack'
}

const ZONE_RANK = { 'own-half': 0, attack: 1, 'dangerous-attack': 2 }

/**
 * Get the zone from the event type. If the event doesn't map to a zone
 * (e.g. Corner, Frappe), keep the current phase zone.
 */
function getZoneFromEvent(eventType, currentZone) {
  return EVENT_TO_ZONE[eventType] || currentZone || 'own-half'
}

/**
 * Infer how the team recovered the ball.
 * Based on the previous phase's last event + the current first event.
 */
function inferRecoveryType(previousPhase, firstEventType) {
  if (firstEventType === "Coup d'envoi" || firstEventType === "Coup d'envoi 2ème mi-temps") {
    return 'kickoff'
  }
  if (firstEventType === 'Touche') return 'throw-in'
  if (firstEventType === 'Corner') return 'corner'
  if (firstEventType === 'Coup franc' || firstEventType === 'Coup franc dangereux') return 'free-kick'
  if (firstEventType === '6 mètres') return 'goal-kick'

  if (!previousPhase || previousPhase.events.length === 0) return 'kickoff'

  const lastOppEvent = previousPhase.events[previousPhase.events.length - 1]?.type
  if (lastOppEvent === 'Frappe cadrée' || lastOppEvent === 'Frappe non cadrée') {
    return 'save-or-miss'
  }
  if (lastOppEvent === 'Hors-jeu') return 'offside'
  if (lastOppEvent === 'Ballons perdus') return 'opponent-turnover'

  return 'turnover'
}

/**
 * Read current player info from state, enriched via playerMapping.
 */
function readCurrentPlayer() {
  const info = state.lastPlayerInfo
  if (!info?.playerName) return { name: null, id: null }

  let name = info.playerName
  const id = info.playerId || null

  if (state.playerMapping && id) {
    const mapped = state.playerMapping[id]
    if (mapped) {
      name = mapped.shortName || mapped.name || name
    }
  }

  return { name, id }
}

export class PossessionPhaseTracker {
  constructor() {
    this.currentPhase = null
    this.archivedPhases = []
    this._previousTeam = null
  }

  /**
   * Main entry point. Called on every event from ball-controller.
   *
   * @param {string} eventType - Event type from MQTT (Possession, Attaque, Corner, etc.)
   * @param {string} team - 'home', 'away', or 'neutral'
   * @param {number} x - Ball x position (0-1)
   * @param {number} y - Ball y position (0-1)
   */
  onEvent(eventType, team, x, y) {
    // Neutral events (Mi-temps, Fin du match, VAR, etc.) don't change phase
    if (!team || team === 'neutral') return

    const currentZone = this.currentPhase?.currentZone || null
    const zone = getZoneFromEvent(eventType, currentZone)
    const player = readCurrentPlayer()

    const isPossessionChange = this._previousTeam && this._previousTeam !== team
    const isPhaseReset = PHASE_RESET_EVENTS.includes(eventType)

    if (!this.currentPhase || isPossessionChange || isPhaseReset) {
      this._startNewPhase(eventType, team, x, y, zone, player)
    } else {
      this._addEvent(eventType, zone, player, x, y)
    }

    this._previousTeam = team
  }

  _startNewPhase(eventType, team, x, y, zone, player) {
    // Archive current phase
    if (this.currentPhase) {
      this.archivedPhases.push(this.currentPhase)
      if (this.archivedPhases.length > MAX_ARCHIVED_PHASES) {
        this.archivedPhases.shift()
      }
    }

    const previousPhase = this.archivedPhases[this.archivedPhases.length - 1] || null
    const recoveryType = inferRecoveryType(previousPhase, eventType)

    this.currentPhase = {
      team,
      startTime: Date.now(),
      recoveryType,
      startZone: zone,
      startPosition: { x, y },
      startPlayer: player.name,
      events: [],
      passCount: 0,
      currentZone: zone,
      lastPlayer: player.name,
      score: state.lastScore || '0-0',
      highestZone: zone
    }

    // Add the triggering event
    this._addEvent(eventType, zone, player, x, y)
  }

  _addEvent(eventType, zone, player, x, y) {
    if (!this.currentPhase) return

    this.currentPhase.events.push({
      type: eventType,
      timestamp: Date.now(),
      player: player.name,
      playerId: player.id,
      position: { x, y },
      zone
    })

    this.currentPhase.currentZone = zone
    if (player.name) {
      this.currentPhase.lastPlayer = player.name
    }

    // Count ball progression events
    if (PROGRESSION_EVENTS.includes(eventType)) {
      this.currentPhase.passCount++
    }

    // Track highest zone reached
    if ((ZONE_RANK[zone] ?? -1) > (ZONE_RANK[this.currentPhase.highestZone] ?? -1)) {
      this.currentPhase.highestZone = zone
    }
  }

  /**
   * Get duration of current phase in seconds.
   */
  getPhaseDurationSec() {
    if (!this.currentPhase) return 0
    return Math.round((Date.now() - this.currentPhase.startTime) / 1000)
  }

  /**
   * Get the current phase data.
   */
  getCurrentPhase() {
    return this.currentPhase
  }

  /**
   * Get archived phases (most recent last).
   */
  getArchivedPhases() {
    return this.archivedPhases
  }

  /**
   * Reset everything.
   */
  reset() {
    this.currentPhase = null
    this.archivedPhases = []
    this._previousTeam = null
  }
}
